import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { T } from '../../i18n/copy';
import { clinicPath, homePath } from '../../i18n/paths';
import ImageSlot from '../ImageSlot';
import heroImg from '../../assets/hero-golden-hour-boardroom.jpg';

const lineAnim = (i) => ({
  initial: { y: '112%' },
  animate: { y: '0%' },
  transition: { duration: 1.05, delay: 0.25 + i * 0.11, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero({ lang }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 56]);
  const t = T.hero;
  return (
    <section ref={ref} data-header-theme="bone" data-testid="hero-section" className="relative bg-bone">
      <div className="editorial-margins grid grid-cols-1 gap-12 py-8 md:py-10 lg:grid-cols-12 lg:gap-10">
        <div className="flex flex-col justify-center lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-3"
          >
            <span className="inline-block h-2 w-2 bg-vermilion" aria-hidden="true" />
            <span className="eyebrow text-midnight/70">{t.eyebrow[lang]}</span>
            <span className="eyebrow ml-auto hidden text-steel-dark md:inline">{t.index}</span>
          </motion.div>

          <h1 className="mt-6 font-tight font-semibold text-[clamp(2.9rem,5.9vw,5.4rem)] leading-[0.98] tracking-[-0.025em] text-midnight">
            {t.headlineLines[lang].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1" data-testid={`hero-headline-line-${i}`}>
                <motion.span className="block will-change-transform" {...lineAnim(i)}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.72, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 max-w-md text-base leading-relaxed text-midnight/70"
          >
            {t.body[lang]}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-4 sm:pt-2"
          >
            <Link
              to={clinicPath(lang)}
              data-testid="hero-cta-button"
              className="group inline-flex items-center justify-center gap-3 bg-vermilion px-7 py-4 text-[13px] font-medium tracking-[0.06em] text-bone transition-colors duration-300 hover:bg-[#A8402C]"
            >
              {t.ctaPrimary[lang]}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </Link>
            <Link
              to={`${homePath(lang)}#how-we-work`}
              data-testid="hero-secondary-button"
              className="hidden items-center justify-center gap-3 border border-midnight/25 px-7 py-4 text-[13px] font-medium tracking-[0.06em] text-midnight transition-colors duration-300 hover:border-midnight hover:bg-midnight hover:text-bone sm:inline-flex"
            >
              {t.ctaSecondary[lang]}
            </Link>
            <Link
              to={`${homePath(lang)}#how-we-work`}
              data-testid="hero-secondary-link"
              className="group inline-flex items-center gap-2 border-b border-midnight/25 pb-1 text-[15px] font-medium text-midnight transition-colors duration-300 hover:border-vermilion hover:text-vermilion sm:hidden"
            >
              {t.ctaSecondary[lang]}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-midnight/10 pt-4 md:mt-8"
            data-testid="hero-supporting-line"
          >
            {t.supporting[lang].map((w, i) => (
              <span key={w} className="flex items-center gap-3">
                <span className="eyebrow text-midnight/60">{w}</span>
                {i < 3 && <ArrowRight className="h-3 w-3 text-vermilion" strokeWidth={1.5} />}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative -mx-6 lg:col-span-5 lg:ml-0 lg:-mr-24">
          <div className="relative h-full min-h-[380px] overflow-hidden lg:min-h-[500px]">
            <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
              <ImageSlot slotKey="hero" fill src={heroImg} alt="Conceptual editorial boardroom at golden hour with a city skyline — not a Navartha office" imgClassName="object-[60%_center]" className="h-full w-full scale-[1.06]" />
              <div className="pointer-events-none absolute inset-0 bg-midnight/10" aria-hidden="true" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
